import type { Probe, Finding, ProbeResult } from '../engine/types';
import { ok } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { sig } from '../engine/normalize';

const RESOURCE_TIMING = 'https://www.w3.org/TR/resource-timing/';
const DNS_REBIND = 'https://portswigger.net/research/dns-rebinding';
const HE = 'https://datatracker.ietf.org/doc/html/rfc8305';
const RFC7873 = 'https://datatracker.ietf.org/doc/html/rfc7873';
const RFC7871 = 'https://datatracker.ietf.org/doc/html/rfc7871';
const RFC8482 = 'https://datatracker.ietf.org/doc/html/rfc8482';
const PNA = 'https://developer.chrome.com/blog/private-network-access-preflight/';

/** A third-party host used ONLY to test cross-origin timing visibility. */
const CROSS_ORIGIN_PROBE_URL = 'https://example.com/';

/**
 * DNS-layer and resolver-behaviour probe (dns-cache-fingerprinting.md).
 *
 * WHAT IS ACTUALLY OBSERVABLE FROM A PAGE — established empirically, not
 * assumed:
 *
 *   - SAME-ORIGIN requests DO expose real DNS timing through
 *     PerformanceResourceTiming.domainLookupStart/End. Verified in Chromium.
 *   - An entry with NO DNS lookup (the host was cached, or the connection was
 *     reused) has domainLookupStart === domainLookupEnd === fetchStart, all
 *     NON-ZERO. The start is not "0": it is stamped with fetchStart.
 *   - domainLookupStart === 0 AND domainLookupEnd === 0 means the entry was
 *     CLAMPED — a cross-origin response without Timing-Allow-Origin. It is not
 *     evidence of a cache hit. Verified in Chromium: a same-origin entry
 *     reported ~10063.9 for all three fields, while a cross-origin entry
 *     reported 0/0 with a non-zero fetchStart.
 *
 * That distinction shapes this whole probe. The DNS-layer techniques in the
 * source document — 0x20 case randomisation, EDNS0 buffer size and DO bit,
 * ECS subnet, DNS cache snooping, the DoH canary query — are all resolver-side
 * or network-position signals. A page cannot read its own resolver's query
 * packets, cannot see EDNS options, and cannot see which canary domain the
 * browser's internal resolver queried. Those are catalogued as server-tier.
 *
 * WHAT WE CAN HONESTLY MEASURE, which the source document does not list:
 *
 *   1. DNS cache presence. domainLookupEnd === domainLookupStart (with a
 *      non-zero start) on a same-origin entry means the browser resolved
 *      nothing — the name was already cached (or the connection was reused).
 *      Comparing a cold-ish request against a repeat is the client-side
 *      residue of the source doc's "does this client even cache?" technique.
 *   2. Connection reuse vs re-resolution, which separates a browser with a
 *      real network stack from a naive script that re-resolves every time.
 *
 * The "naive script" inference is deliberately NOT drawn as a verdict: every
 * browser caches, so a single observation cannot separate a browser from a
 * caching script. We report the measurements and let a reader correlate.
 */
export const dnsProbe: Probe = {
  id: 'dnsBehaviour',
  label: 'DNS & resolver behaviour',
  layer: 'network',
  tier: 'client',
  category: 'network',
  description: 'Same-origin DNS cache presence, connection reuse, and protection-surface capability',
  sources: [RESOURCE_TIMING, DNS_REBIND, HE, RFC7873, RFC7871, RFC8482, PNA],  budgetMs: 5000,

  async run(): Promise<ProbeResult> {
    const findings: Finding[] = [];
    // Cross-origin probing is opt-in: it contacts a third-party host, and the
    // default scan stays strictly same-origin.
    const probeCrossOrigin =
      (import.meta.env?.VITE_DNS_CROSS_ORIGIN_PROBE as string | undefined) === '1';

    // --- 1. same-origin DNS timing + cache presence ------------------------
    // Each request carries a distinct query string, so the RESOURCE is unique,
    // but the HOST is the same. If no DNS lookup is performed (the name is
    // cached, or the connection is reused) domainLookupEnd === domainLookupStart
    // with a non-zero start; if the browser re-resolves, the end is later and
    // the start is stamped before it. All-zero start AND end is the clamped
    // cross-origin case, which we count separately rather than mistaking for a
    // cache hit.
    const marker = `dnsprobe-${Math.random().toString(36).slice(2, 8)}`;

    // Resource-Timing entries are appended a tick (or more) after fetch()
    // resolves, and the buffer is shared with ~45 other concurrent probes, so a
    // read immediately after fetch can miss the entry. An observer reports it,
    // but its callback is itself a task, so we wait for each entry by a
    // deadline, checking both the observer's map and the shared buffer.
    const entries = new Map<string, PerformanceResourceTiming>();
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name.includes(`${marker}=1`)) {
          const e = entry as PerformanceResourceTiming;
          const m = /i=(\d+)/.exec(e.name);
          entries.set(m?.[1] ?? '0', e);
        }
      }
    });
    try {
      observer.observe({ type: 'resource', buffered: true });
    } catch {
      // Type not supported: the buffer scan in findEntry is the only source.
    }

    const findEntry = (i: number): PerformanceResourceTiming | undefined =>
      entries.get(String(i)) ??
      (performance
        .getEntriesByType('resource')
        .filter((e) => e.name.includes(`${marker}=1`) && e.name.includes(`i=${i}`))
        .pop() as PerformanceResourceTiming | undefined);

    const waitForEntry = async (
      i: number,
      timeoutMs = 600,
    ): Promise<PerformanceResourceTiming | undefined> => {
      const deadline = performance.now() + timeoutMs;
      for (;;) {
        const hit = findEntry(i);
        if (hit) return hit;
        if (performance.now() >= deadline) return undefined;
        await new Promise((r) => setTimeout(r, 10));
      }
    };

    const timings: Array<{
      iteration: number;
      domainLookupStart: number;
      domainLookupEnd: number;
      domainLookupMs: number | null;
      connectMs: number | null;
      transferSize: number;
      fromCache: boolean;
      clamped: boolean;
    }> = [];

    for (let i = 0; i < 3; i++) {
      const probeUrl = `/_headers?${marker}=1&i=${i}&t=${Date.now()}`;
      try {
        await fetch(probeUrl, { cache: 'no-store' });
      } catch {
        /* keep going; a failed sample is recorded as a gap */
      }
      const entry = await waitForEntry(i);
      if (entry) {
        const dls = entry.domainLookupStart;
        const dle = entry.domainLookupEnd;
        // All-zero start AND end is the clamped cross-origin case, never a
        // cache hit: a same-origin no-lookup entry reports both as fetchStart.
        const clamped = dls === 0 && dle === 0;
        timings.push({
          iteration: i,
          domainLookupStart: dls,
          domainLookupEnd: dle,
          domainLookupMs: dls === 0 && dle === 0 ? 0 : sig(Math.max(0, dle - dls), 3),
          connectMs: sig(Math.max(0, entry.connectEnd - entry.connectStart), 3),
          transferSize: entry.transferSize,
          // No lookup performed: start and end are the SAME non-zero timestamp.
          fromCache: !clamped && dls > 0 && dle === dls,
          clamped,
        });
      }
    }
    // Stop observing; the entries we needed are already captured.
    try {
      observer.disconnect();
    } catch {
      /* already disconnected */
    }

    // A real lookup means domainLookupEnd is LATER than domainLookupStart.
    // A same-origin no-lookup entry has both equal to fetchStart (non-zero);
    // a clamped cross-origin entry has both at 0.
    const dnsObserved = timings.some((t) => !t.clamped && t.domainLookupEnd > t.domainLookupStart);
    const anyFromCache = timings.some((t) => t.fromCache);
    const anyClamped = timings.some((t) => t.clamped);
    const anyZeroTransfer = timings.some((t) => t.transferSize === 0);

    // --- 2. cross-origin DNS timing availability (expected: unavailable) ---
    // Verified: without Timing-Allow-Origin the values are clamped to 0, so
    // cross-origin resolver fingerprinting is not possible from a page.
    //
    // This makes ONE opt-in request to a third-party host, purely to observe
    // whether that host permits timing visibility. It is a no-cors request with
    // no credentials and no payload, and it is disabled by default so the
    // default scan remains strictly same-origin.
    let crossOriginDnsVisible = false;
    let crossOriginMs: number | null = null;
    if (probeCrossOrigin) {
      try {
        const t0 = performance.now();
        const r = await fetch(CROSS_ORIGIN_PROBE_URL, {
          mode: 'no-cors',
          cache: 'no-store',
        });
        crossOriginMs = sig(performance.now() - t0, 2);
        const e = performance
          .getEntriesByType('resource')
          .filter((x) => x.name.startsWith(CROSS_ORIGIN_PROBE_URL))
          .pop() as PerformanceResourceTiming | undefined;
        crossOriginDnsVisible = !!e && e.domainLookupStart > 0;
        void r;
      } catch {
        // Offline or blocked; cross-origin DNS timing stays unavailable.
      }
    }

    if (crossOriginDnsVisible) {
      // If this ever becomes true, the cross-origin resolver signals in the
      // source document become partially reachable, and that is worth knowing.
      findings.push({
        id: 'dns.crossorigin-timing-visible',
        severity: 'info',
        category: 'network',
        layer: 'network',
        confidence: 'medium',
        title: 'Cross-origin DNS timing is visible (Timing-Allow-Origin present)',
        detail:
          'This origin sent Timing-Allow-Origin, so cross-origin domainLookupStart is populated rather than clamped to 0. That makes some resolver-layer timing signals reachable from the page, which is normally not the case.',
        sources: [RESOURCE_TIMING],
        evidence: ['cross-origin domainLookupStart > 0'],
        scoreContribution: 0,
      });
    }

    return {
      signals: {
        available: ok(true, 'client'),
        // Same-origin DNS resolution behaviour
        sameOriginDnsObserved: ok(dnsObserved, 'derived'),
        sameOriginDnsSampleCount: ok(timings.length, 'client'),
        // WHY it might be empty — so a reader is never left guessing.
        dnsSampleDiagnostic: ok(
          timings.length > 0
            ? 'entries captured'
            : 'fetch produced no matching Resource-Timing entry (observer unsupported, or the entry was evicted before read)',
          'derived',
        ),
        servedFromDnsCache: ok(anyFromCache, 'derived'),
        // Clamped (all-zero) entries can only come from a cross-origin response
        // without Timing-Allow-Origin; they must not be read as cache hits.
        clampedEntriesObserved: ok(anyClamped, 'derived'),
        zeroTransferObserved: ok(anyZeroTransfer, 'derived'),
        domainLookupStartMs: ok(timings.map((t) => t.domainLookupStart), 'client'),
        domainLookupEndMs: ok(timings.map((t) => t.domainLookupEnd), 'client'),
        domainLookupMs: ok(timings.map((t) => t.domainLookupMs), 'client'),
        connectMs: ok(timings.map((t) => t.connectMs), 'client'),
        // Cross-origin resolver visibility is opt-in. Its signals are reported
        // ONLY when the probe is enabled; otherwise they were a constant
        // false/null trio on every default deployment.
        ...(probeCrossOrigin
          ? {
              crossOriginDnsVisible: ok(crossOriginDnsVisible, 'derived'),
              crossOriginRoundTripMs: ok(crossOriginMs, 'client'),
            }
          : {}),
        note: ok(
          'Resolver-side signals (0x20 case randomisation, EDNS0 buffer size / DO bit / option order, ECS subnet, DoH canary query, DNS cache snooping) are NOT observable from a page. They are catalogued as server-tier. Timing semantics: a real lookup shows domainLookupEnd > domainLookupStart; no lookup shows both equal and non-zero (=== fetchStart); all-zero values are cross-origin entries clamped without Timing-Allow-Origin.',
          'client',
        ),
      },
      findings,
      // NO part contribution. DNS timing tracks the network path and the local
      // resolver's cache state at that moment, so hashing it would make the
      // fingerprint drift with network conditions.
    };
  },
};

registerProbe(dnsProbe);
